import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/app/back-button';
import SheetReportUpdate from './_partials/sheets/SheetReportUpdate';
import SheetReportFinalize from './_partials/sheets/SheetReportFinalize';
import AlertDialogExitConfirmation from '@/pages/_partials/AlertDialogExitConfirmation';
import { useProcedureReport } from '@/lib/hooks/use-procedure-report';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useExitConfirmation } from '@/lib/hooks/use-exit-confirmation';
import { useBalance } from '@/lib/contexts/balance-context';
import { backendToFrontendFieldType } from '@/lib/utils/procedure-field-types';
import ProcedureReportForm, {
    LoadingSkeleton,
    ErrorState,
} from '@/pages/procedures/reports/_partials/form/ProcedureReportForm';
import { useProcedureReportForm } from '@/pages/procedures/reports/_partials/form/useProcedureReportForm';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/**
 * Transforme les données du rapport pour l'édition.
 * Extrait la configuration, les answers et construit un formData flat + les photos existantes.
 */
function transformReportForEditing(report) {
    if (!report) return null;

    const procedureReport = report.procedureReport;
    const templateVersion = procedureReport?.procedureTemplateVersion;
    const configuration = templateVersion?.configuration || procedureReport?.configuration;
    const answers = procedureReport?.answers;

    // formData flat { fieldId: value }
    const initialFormData = {};
    // photos existantes { fieldId: [{ uniq_id, previewUrl }] }
    const initialPhotos = {};

    const sections = (configuration?.sections || []).map((section, sectionIndex) => {
        const answerSection = answers?.sections?.find((as) => as.id === section.id) || {};

        (section.fields || []).forEach((field) => {
            const answerField = answerSection.fields?.find((af) => af.id === field.id);
            const frontendType = backendToFrontendFieldType(field.type);

            if (frontendType === 'photo' || frontendType === 'video') {
                // Récupérer les médias existants depuis les documents du rapport
                const docIds = answerField?.value || [];
                if (Array.isArray(docIds) && docIds.length > 0) {
                    initialPhotos[field.id] = docIds
                        .map((docId) => {
                            const doc = report.documents?.find((d) => d.uniq_id === docId);
                            if (!doc) return null;
                            const mediaType = doc.mime?.startsWith('video/') ? 'video' : 'photo';
                            return {
                                uniq_id: doc.uniq_id,
                                previewUrl: `/api/gateway/api/pictures/${doc.uniq_id}`,
                                mediaType,
                                mime: doc.mime,
                            };
                        })
                        .filter(Boolean);
                }
            } else {
                initialFormData[field.id] = answerField?.value ?? field.default ?? '';
            }
        });

        return section;
    });

    return {
        id: report.uniq_id,
        title: report.name || i18n.t('procedures:rapportDeProcedure'),
        color: procedureReport?.color || configuration?.color || 'var(--secondary)',
        configuration,
        initialFormData,
        initialPhotos,
    };
}

export default function ProcedureReportEdit() {
    const { t } = useTranslation();
    const { procedureId, reportId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshBalance } = useBalance();
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

    const exitPath = location.state?.returnTo || `/procedures/${procedureId}/reports/${reportId}`;
    const handleBackToReport = () => navigate(-1);
    const currentUrl = location.pathname;

    // Sheets
    const [showUpdateSheet, setShowUpdateSheet] = useState(false);
    const [showFinalizeSheet, setShowFinalizeSheet] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Récupération du rapport
    const { report, isLoading, error, notFound, refetch } = useProcedureReport(procedureId, reportId);

    const folderId = report?.folder_uniq_id;
    const interventionId = report?.uniq_id;
    const isDraft = report?.procedureReport?.status === 'draft';

    // Transformer les données pour l'édition
    const reportData = useMemo(() => transformReportForEditing(report), [report]);

    // Hook formulaire partagé — initialisé avec les données transformées
    const {
        formData,
        setFormData,
        uploadedPhotos,
        setUploadedPhotos,
        sections,
        totalPhotoCount,
        handleFieldChange,
        showPhotoCapture,
        captureFieldId,
        isUploadingPhoto,
        handlePhotoCapture,
        handlePhotoCaptured,
        handleRemovePhoto,
        closePhotoCapture,
        buildAnswersWithPhotos,
        isSectionValid,
    } = useProcedureReportForm({
        folderId,
        interventionId,
        configuration: reportData?.configuration,
        initialFormData: reportData?.initialFormData || {},
        initialPhotos: reportData?.initialPhotos || {},
    });

    // Ré-initialiser quand reportData change (nouveau rapport chargé)
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    useEffect(() => {
        if (reportData && !isFormInitialized) {
            skipNextChangeRef.current = true;
            setFormData(reportData.initialFormData);
            setUploadedPhotos(reportData.initialPhotos);
            setIsFormInitialized(true);
        }
    }, [reportData, isFormInitialized]);

    useEffect(() => {
        setIsFormInitialized(false);
        setHasUserEdited(false);
        skipNextChangeRef.current = false;
    }, [reportId]);

    // Sauvegarder le brouillon avant de quitter
    const saveDraftBeforeExit = useCallback(async () => {
        if (!folderId || !interventionId) return;
        try {
            const answers = buildAnswersWithPhotos();
            if (!answers) return;
            await horodatyApi.updateProcedureReport(folderId, interventionId, {
                answers,
                name: reportData?.title,
            });
            toast.success(t('procedures:brouillonSauvegarde'));
        } catch (err) {
            console.error('Erreur sauvegarde brouillon:', err);
        }
    }, [folderId, interventionId, buildAnswersWithPhotos, reportData?.title, t]);

    // Tracker si le brouillon vient d'être sauvegardé (pas de confirmation de sortie)
    const [isDraftJustSaved, setIsDraftJustSaved] = useState(false);
    // Ref pour ignorer le changement de formData/uploadedPhotos causé par l'initialisation
    const skipNextChangeRef = useRef(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    useEffect(() => {
        if (skipNextChangeRef.current) {
            skipNextChangeRef.current = false;
            return;
        }
        if (!isFormInitialized) return;
        setHasUserEdited(true);
        setIsDraftJustSaved(false);
    }, [formData, uploadedPhotos]);

    // Confirmation de sortie (uniquement pour les brouillons avec modifs en attente)
    const hasUnsavedChanges = isDraft && hasUserEdited && !isDraftJustSaved;
    const { isDialogOpen, setIsDialogOpen, confirmExit, exitWithoutSave, requestExit, isSaving } = useExitConfirmation(
        hasUnsavedChanges,
        exitPath,
        { onBeforeExit: saveDraftBeforeExit },
    );

    // ============ NAVIGATION ============
    const currentSection = sections[currentSectionIndex];
    const totalSections = sections.length;

    const handlePrev = () => {
        if (currentSectionIndex > 0) {
            setCurrentSectionIndex(currentSectionIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentSectionIndex < totalSections - 1) {
            setCurrentSectionIndex(currentSectionIndex + 1);
        } else if (isDraft) {
            setShowFinalizeSheet(true);
        } else {
            handleUpdateReport();
        }
    };

    // ============ MISE À JOUR ============
    const handleUpdateReport = async () => {
        if (!folderId || !interventionId) {
            toast.error(t('procedures:erreurDonneesManquantes'));
            return;
        }
        setIsUpdating(true);
        try {
            const answers = buildAnswersWithPhotos();
            if (!answers) {
                toast.error(t('procedures:erreurImpossibleDeConstruireLesReponses'));
                return;
            }
            await horodatyApi.updateProcedureReport(folderId, interventionId, {
                answers,
                name: reportData.title,
            });
            toast.success(t('procedures:rapportMisAJourAvecSucces'));
            refreshBalance();
            setShowUpdateSheet(true);
        } catch (err) {
            console.error('Erreur mise à jour:', err);
            toast.error(err.response?.data?.message || t('procedures:erreurLorsDeLaMiseAJour'));
        } finally {
            setIsUpdating(false);
        }
    };

    // ============ FINALISATION ============
    const handleFinalizeDraft = async ({ appendCertificates } = {}) => {
        if (!folderId || !interventionId || !isDraft) return;
        setIsFinalizing(true);
        try {
            const answers = buildAnswersWithPhotos();
            if (!answers) {
                toast.error(t('procedures:erreurImpossibleDeConstruireLesReponses'));
                return;
            }
            await horodatyApi.finalizeProcedureReportDraft(folderId, interventionId, {
                answers,
                name: reportData.title,
                append_certificates: appendCertificates ?? false,
            });
            toast.success(t('procedures:rapportFinaliseAvecSucces'));
            refreshBalance();
            navigate(`/procedures/${procedureId}/reports/${reportId}`, { replace: true });
        } catch (err) {
            console.error('Erreur finalisation:', err);
            toast.error(err.response?.data?.message || t('procedures:erreurLorsDeLaFinalisation'));
        } finally {
            setIsFinalizing(false);
            setShowFinalizeSheet(false);
        }
    };

    // ============ RENDU ============
    if (isLoading) return <LoadingSkeleton onBack={handleBackToReport} />;

    if (error || notFound || !reportData) {
        return (
            <ErrorState
                message={notFound ? t('procedures:rapportNonTrouve') : t('procedures:erreurLorsDuChargementDuRapport')}
                onRetry={() => refetch()}
                onBack={handleBackToReport}
            />
        );
    }

    // Bloquer l'édition si le rapport n'est pas un brouillon
    if (!isDraft) {
        return (
            <div className="bg-surface relative size-full overflow-hidden flex flex-col">
                <div className="bg-card shadow-sm px-[20px] py-[16px] flex items-center gap-[12px]">
                    <BackButton onClick={handleBackToReport} />
                    <h1 className="text-[18px] text-foreground">{reportData.title}</h1>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center px-6">
                    <AlertCircle className="w-12 h-12 text-warning mb-4" />
                    <p className="text-[16px] text-foreground font-medium text-center mb-2">{t('procedures:rapportNonModifiable')}</p>
                    <p className="text-muted-foreground text-center text-sm mb-6">
                        {t('procedures:ceRapportADejaEteEnregistreEtNe')}
                    </p>
                    <Button
                        variant="solid"
                        color="secondary"
                        onClick={() => navigate(`/procedures/${procedureId}/reports/${reportId}`)}
                    >
                        {t('procedures:voirLeRapport')}
                    </Button>
                </div>
            </div>
        );
    }

    if (totalSections === 0) {
        return <ErrorState message="Aucune section à éditer dans ce rapport" onBack={handleBackToReport} />;
    }

    const canProceed = currentSection ? isSectionValid(currentSection) : false;
    const isLastSection = currentSectionIndex === totalSections - 1;
    const lastButtonLabel = isDraft ? t('procedures:terminer') : t('procedures:mettreAJour');

    return (
        <ProcedureReportForm
            title={reportData.title}
            color={reportData.color}
            currentSection={currentSection}
            currentSectionIndex={currentSectionIndex}
            totalSections={totalSections}
            formData={formData}
            uploadedPhotos={uploadedPhotos}
            isUploadingPhoto={isUploadingPhoto}
            showPhotoCapture={showPhotoCapture}
            captureFieldId={captureFieldId}
            onFieldChange={handleFieldChange}
            onPhotoCapture={handlePhotoCapture}
            onPhotoCaptured={handlePhotoCaptured}
            onRemovePhoto={handleRemovePhoto}
            onClosePhotoCapture={closePhotoCapture}
            onNext={handleNext}
            onPrev={handlePrev}
            onBack={hasUnsavedChanges ? requestExit : handleBackToReport}
            canProceed={canProceed}
            isSubmitting={isUpdating}
            lastButtonLabel={lastButtonLabel}
            stepLabel={`Section ${currentSectionIndex + 1} sur ${totalSections}`}
            mode="edit"
            sourceUrl={currentUrl}
            afterContent={
                <>
                    <AlertDialogExitConfirmation
                        open={isDialogOpen}
                        onOpenChange={setIsDialogOpen}
                        onConfirm={confirmExit}
                        onExitWithoutSave={exitWithoutSave}
                        isSaving={isSaving}
                        saveOnExit={isDraft}
                    />
                    <SheetReportUpdate
                        open={showUpdateSheet}
                        onClose={() => setShowUpdateSheet(false)}
                        onUpdate={() => navigate(`/procedures/${procedureId}/reports/${reportId}`)}
                        reportName={reportData.title}
                    />
                    <SheetReportFinalize
                        open={showFinalizeSheet}
                        onClose={() => setShowFinalizeSheet(false)}
                        onFinalize={handleFinalizeDraft}
                        onSaveDraft={async () => {
                            setShowFinalizeSheet(false);
                            await saveDraftBeforeExit();
                            setIsDraftJustSaved(true);
                        }}
                        isFinalizing={isFinalizing}
                        photoCount={totalPhotoCount}
                        tokenCostPdf={2}
                        reportName={reportData.title}
                        folderName={report?.folder?.name || ''}
                    />
                </>
            }
        />
    );
}
